/*
 * Lemonade Training Oy copy rights all right reserved.
 * Movement Registry Type Definitions
 */

import { Timestamp } from "firebase/firestore";

/**
 * Radar chart axis identifiers
 * Maps to the 5 axes of the pentagon radar chart
 */
export const RADAR_AXIS = {
  STR: "STR",
  OLY: "OLY",
  GYM: "GYM",
  END: "END",
  WC: "WC",
} as const;

export type RadarAxis = (typeof RADAR_AXIS)[keyof typeof RADAR_AXIS];

/** All radar axes as an array, useful for iteration */
export const RADAR_AXES: readonly RadarAxis[] = [
  "STR",
  "OLY",
  "GYM",
  "END",
  "WC",
] as const;

/**
 * Scoring **shape** — the only thing the V1 scoring algorithm branches on.
 *
 * Refactored in Amendment 1 ([LT-523](https://linear.app/lemonade-training/issue/LT-523))
 * from the pre-Amendment domain model (`weight | reps | time | binary`) to a
 * scoring-shape model. The algorithm only ever decides forward-interpolate,
 * reverse-interpolate, or unlock — `weight` and `reps` were always the same code
 * path, and `time` conflated "Fran (faster is better)" with "max hold (longer is
 * better)", reverse-scoring every isometric hold. Modelling the *shape* (not the
 * movement domain) fixes that bug and closes the engine to future measure-type
 * churn.
 *
 * The orthogonal **unit** lives in {@link MOVEMENT_MEASURE_TYPE}. See
 * `features/next-gen-athlete-mvp/SCORING-V1.md` §3.1 (coherence matrix) and §14.
 *
 * - `higherIsBetter` — forward interpolation (kg, reps, calories, distance, time-holds)
 * - `lowerIsBetter` — reverse interpolation (time "for time": Fran, 2k row)
 * - `binary` — unlock scoring (gymnastics achievements; label deliberately kept)
 */
export const SCORING_TYPE = {
  HIGHER_IS_BETTER: "higherIsBetter",
  LOWER_IS_BETTER: "lowerIsBetter",
  BINARY: "binary",
} as const;

export type ScoringType = (typeof SCORING_TYPE)[keyof typeof SCORING_TYPE];

/**
 * Movement measure **unit** — what is shown to the athlete. Orthogonal to
 * {@link ScoringType} (the scoring shape the algorithm branches on).
 *
 * Promoted to a first-class type in Amendment 1
 * ([LT-523](https://linear.app/lemonade-training/issue/LT-523)) to stop the
 * scattered inline `"kg" | "reps" | "time" | "binary"` union and to add the
 * `calories` / `distance` units CrossFit erg & handstand-walk benchmarking
 * needs (closes the SCORING-V1 §3.2.5 `Handstand Walk (Distance)` gap).
 *
 * Coherence with `ScoringType` (kg/reps/calories/distance → `higherIsBetter`;
 * `time` → `higherIsBetter` **or** `lowerIsBetter`, explicit; `binary` →
 * `binary`) is enforced server-side in Cloud Functions, NOT in this types-only
 * package. See SCORING-V1.md §3.1.
 */
export const MOVEMENT_MEASURE_TYPE = {
  KG: "kg",
  REPS: "reps",
  TIME: "time",
  CALORIES: "calories",
  DISTANCE: "distance",
  BINARY: "binary",
} as const;

export type MovementMeasureType =
  (typeof MOVEMENT_MEASURE_TYPE)[keyof typeof MOVEMENT_MEASURE_TYPE];

/** All movement measure units as an array, useful for iteration/validation. */
export const MOVEMENT_MEASURE_TYPES: readonly MovementMeasureType[] = [
  "kg",
  "reps",
  "time",
  "calories",
  "distance",
  "binary",
] as const;

/**
 * Type guard for {@link MovementMeasureType}.
 *
 * @example
 * ```typescript
 * if (isMovementMeasureType(raw)) {
 *   // TypeScript narrows `raw` to MovementMeasureType
 * }
 * ```
 */
export function isMovementMeasureType(
  obj: unknown,
): obj is MovementMeasureType {
  return (
    typeof obj === "string" &&
    (MOVEMENT_MEASURE_TYPES as readonly string[]).includes(obj)
  );
}

/**
 * Movement category for filtering and organization
 * Aligned with radar axes
 */
export const MOVEMENT_REGISTRY_CATEGORY = {
  STRENGTH: "strength",
  OLYMPIC: "olympic",
  GYMNASTICS: "gymnastics",
  ENDURANCE: "endurance",
  WORK_CAPACITY: "workCapacity",
} as const;

export type MovementRegistryCategory =
  (typeof MOVEMENT_REGISTRY_CATEGORY)[keyof typeof MOVEMENT_REGISTRY_CATEGORY];

/**
 * Score tiers anchored in the V1 scoring algorithm. The `base` table maps
 * each tier to the target value (kg / reps / seconds / 1) at that score.
 * Movements may seed only some tiers — e.g., binary movements seed `{60: 1}`.
 * See `features/next-gen-athlete-mvp/SCORING-V1.md` §3 (V1 algorithm) and §9
 * (locked runtime shape).
 */
export type ScoringTier = 0 | 20 | 40 | 60 | 80 | 100;

/**
 * Bodyweight bands used by the demographic multiplier in V1 scoring.
 * Aligned with `WEIGHT_CLASSES` in `LemonadeTraining-web/functions/radarScoring/demographics.js` —
 * keep both lists in sync.
 */
export type WeightClass =
  "≤60" | "61-70" | "71-80" | "81-90" | "91-100" | "100+";

/**
 * Benchmark standards for a movement, in the SCORING-V1 §9 runtime shape.
 *
 * - `base` maps tier (0/20/40/60/80/100) → target value at that score.
 * - `modifiers.gender` and `modifiers.weight` apply scalar multipliers to
 *   `base` per athlete demographics. Missing keys resolve to a neutral 1.0
 *   (see `resolveModifiers` in radarScoring/demographics.js).
 *
 * `gender` keys are runtime-flexible (`Record<string, number>`) because the
 * user-profile gender field includes values like `"prefer_not_to_say"` that
 * are intentionally absent from the modifier dict — they fall through to the
 * neutral 1.0 path. `weight` keys are locked to `WeightClass` because the
 * bands are a fixed scoring contract.
 *
 * @example
 * ```typescript
 * const backSquat: BenchmarkStandards = {
 *   base: { 0: 0, 20: 50, 40: 80, 60: 110, 80: 140, 100: 170 },
 *   modifiers: {
 *     gender: { male: 1.15, female: 0.85 },
 *     weight: {
 *       "≤60": 0.82, "61-70": 0.90, "71-80": 0.96,
 *       "81-90": 1.00, "91-100": 1.05, "100+": 1.08,
 *     },
 *   },
 * };
 * ```
 */
export type BenchmarkStandards = {
  base: Partial<Record<ScoringTier, number>>;
  modifiers: {
    gender?: Record<string, number>;
    weight?: Partial<Record<WeightClass, number>>;
  };
};

/**
 * Scope indicator for movement registry operations
 */
export const MOVEMENT_REGISTRY_SCOPE = {
  GLOBAL: "global",
  BOX: "box",
} as const;

export type MovementRegistryScope =
  (typeof MOVEMENT_REGISTRY_SCOPE)[keyof typeof MOVEMENT_REGISTRY_SCOPE];

/**
 * Movement definition in the two-tier movement registry
 *
 * Stored in one of two locations:
 * - **Global**: `MovementRegistry/{movementID}` — default movements managed by Lemonade admins (`boxID: null`)
 * - **Box-specific**: `Boxes/{boxID}/MovementRegistry/{movementID}` — box customizations (`boxID: boxID`)
 *
 * Both use the same collection name, enabling Firestore collection group queries:
 * `db.collectionGroup('MovementRegistry').where('boxID', 'in', [null, myBoxID])`
 *
 * Represents a hierarchical movement with parent-child relationships
 * and cascade coefficients for estimating related movements.
 *
 * @example
 * ```typescript
 * // Global movement (boxID: null)
 * const frontSquat: MovementRegistryEntry = {
 *   id: "front-squat",
 *   boxID: null,
 *   name: "Front Squat",
 *   nameNormalized: "front squat",
 *   category: "strength",
 *   radarAxis: "STR",
 *   tags: ["squat", "quad-dominant"],
 *   scoringType: "higherIsBetter",
 *   measureType: "kg",
 *   parentMovementID: "back-squat",
 *   parentCoefficient: 0.85,
 *   childMovementIDs: [],
 *   standards: {
 *     base: { 0: 0, 20: 50, 40: 80, 60: 110, 80: 140, 100: 170 },
 *     modifiers: {
 *       gender: { male: 1.15, female: 0.85 },
 *       weight: { "≤60": 0.82, "61-70": 0.90, "71-80": 0.96, "81-90": 1.00, "91-100": 1.05, "100+": 1.08 },
 *     },
 *   },
 *   isDefault: true,
 *   createdBy: "system",
 *   createdAt: Timestamp.now(),
 *   updatedAt: Timestamp.now(),
 * };
 * ```
 */
export type MovementRegistryEntry = {
  /** Unique identifier (used as Firestore document ID) */
  id: string;
  /**
   * Box ID for scoping. Must be explicitly set:
   * - `null` for global movements (top-level `MovementRegistry/`)
   * - Box ID string for box-specific movements (`Boxes/{boxID}/MovementRegistry/`)
   * IMPORTANT: Never omit this field — Firestore `in` queries only match explicit `null`, not absent fields.
   */
  boxID: string | null;
  /** Display name of the movement */
  name: string;
  /** Lowercase normalized name for search */
  nameNormalized: string;
  /** Movement category aligned with radar axes */
  category: MovementRegistryCategory;
  /** Which radar axis this movement contributes to */
  radarAxis: RadarAxis;
  /** Searchable tags for filtering */
  tags: string[];
  /** How results for this movement are scored (the scoring shape) */
  scoringType: ScoringType;
  /** Unit of measurement for results (orthogonal to {@link scoringType}) */
  measureType: MovementMeasureType;
  /** Parent movement ID for cascade relationships, null if root */
  parentMovementID: string | null;
  /** Coefficient to estimate this movement from parent (e.g., 0.85) */
  parentCoefficient: number | null;
  /** Denormalized child movement IDs for tree traversal */
  childMovementIDs: string[];
  /**
   * Benchmark standards for V1 scoring.
   * Nullable because `seed.js` writes `null` for entries authored without a
   * standards block (e.g. early WC movements pending coach review). Scoring
   * paths defensively skip movements with `null`/missing standards rather
   * than throwing, so the data layer reflects this.
   */
  standards: BenchmarkStandards | null;
  /** True for pre-populated default movements */
  isDefault: boolean;
  /** UID of the user who created this movement */
  createdBy: string;
  /** When this movement was created */
  createdAt: Timestamp | Date;
  /** When this movement was last updated */
  updatedAt: Timestamp | Date;
};

/**
 * Type guard to check if an object is a valid MovementRegistryEntry
 *
 * @example
 * ```typescript
 * if (isMovementRegistryEntry(data)) {
 *   console.log(data.name); // TypeScript knows data is MovementRegistryEntry
 * }
 * ```
 */
export function isMovementRegistryEntry(
  obj: unknown,
): obj is MovementRegistryEntry {
  if (typeof obj !== "object" || obj === null) return false;
  const entry = obj as MovementRegistryEntry;
  return (
    typeof entry.id === "string" &&
    typeof entry.name === "string" &&
    typeof entry.radarAxis === "string" &&
    RADAR_AXES.includes(entry.radarAxis) &&
    "boxID" in entry &&
    (entry.boxID === null || typeof entry.boxID === "string")
  );
}

/** Coefficient validation range */
export const COEFFICIENT_RANGE = {
  MIN: 0.01,
  MAX: 5.0,
} as const;

/** Maximum cascade depth to prevent runaway calculations */
export const MAX_CASCADE_DEPTH = 5;

/* -------------- [ API Request Types ] ------------------ */

/**
 * Request payload for creating a movement registry entry
 */
export type CreateMovementRequest = {
  /** Box ID for scoping — null or omitted for global, box ID for box-specific */
  boxID?: string | null;
  /** Movement display name */
  name: string;
  /** Movement category */
  category: MovementRegistryCategory;
  /** Radar axis mapping */
  radarAxis: RadarAxis;
  /** Tags for filtering */
  tags: string[];
  /** Scoring shape */
  scoringType: ScoringType;
  /** Measurement unit */
  measureType: MovementMeasureType;
  /** Parent movement ID (null for root movements) */
  parentMovementID: string | null;
  /** Coefficient relative to parent */
  parentCoefficient: number | null;
  /** Benchmark standards. Omit, or pass null, when authoring without one. */
  standards?: BenchmarkStandards | null;
};

/**
 * Request payload for updating a movement registry entry
 */
export type UpdateMovementRequest = Partial<CreateMovementRequest> & {
  /** Movement ID to update */
  id: string;
};
